import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import {
  Home, BookOpen, Library, FileText, PenLine, MessageCircleQuestion,
  ScrollText, Bell, LogOut, Menu, X, ChevronDown, FileEdit, Send, FilePlus2, Quote,
} from 'lucide-react';
import ReportModal from './ReportModal';
import CelebrationModal from './CelebrationModal';
import AnnouncementModal from './AnnouncementModal';
import QuotationModal from './QuotationModal';
import CourseCompletionCelebration from './CourseCompletionCelebration';
import './AdminLayout.css';
import './ReaderLayout.css';

/**
 * Reader-side layout — right-side collapsible sidebar (RTL).
 * • Sidebar is icon-only by default, expands smoothly on hover (desktop).
 * • On mobile it turns into a slide-in drawer with a hamburger toggle.
 * • Floating "رفع تقرير" pill sits at the bottom-left corner
 *   (bottom-inline-start in RTL) as a quick action from any page.
 * • No top bar — content owns the full viewport width.
 */
const ReaderLayout = ({ children }) => {
  const { user, logout, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [mobileOpen, setMobileOpen] = useState(false);
  const [hoverExpanded, setHoverExpanded] = useState(false);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const [celebrationQuote, setCelebrationQuote] = useState(null);
  const [quotationModalOpen, setQuotationModalOpen] = useState(false);
  const [completedBookData, setCompletedBookData] = useState(null);
  const [courseCelebrationOpen, setCourseCelebrationOpen] = useState(false);
  const [courseCompletedData, setCourseCompletedData] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [openGroup, setOpenGroup] = useState('');
  const [bottomBarMorphed, setBottomBarMorphed] = useState(false);

  // Mobile-only, home-page-only: as the user scrolls the home page, the
  // floating "رفع تقرير" pill at top-left translates rightward, shrinks and
  // dissolves into the hamburger — visually teaching the user that the
  // action now lives inside the sidebar menu.
  useEffect(() => {
    if (location.pathname !== '/profile') { setBottomBarMorphed(false); return; }
    const SCROLL_ENTER = 60;   // px scrolled to trigger the melt animation
    const SCROLL_EXIT  = 20;   // px hysteresis to bring the pill back
    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY || document.documentElement.scrollTop || 0;
        setBottomBarMorphed(prev => (prev ? y > SCROLL_EXIT : y > SCROLL_ENTER));
        ticking = false;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [location.pathname]);

  // Notifications badge
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const fetchUnread = async () => {
      try {
        const res = await api.get('/notifications');
        if (!cancelled) setUnreadCount((res.data.notifications || []).filter(n => !n.is_read).length);
      } catch { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [user, location.pathname]);

  // Close mobile drawer on route change; lock body scroll while open
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [mobileOpen]);

  const handleReportSuccess = useCallback(async (reportInfo) => {
    // Course-fully-completed → lavish course celebration is the star of the
    // moment; suppress the regular quote to avoid stacked modals.
    if (reportInfo?.course_completed) {
      setCourseCompletedData({
        courseTitle: reportInfo.completed_course_title || reportInfo.course_title || '',
      });
      setCourseCelebrationOpen(true);
      if (refreshProfile) refreshProfile();
      return;
    }
    // Book completed → quotation modal first, then celebration
    if (reportInfo?.is_completed === 'نعم' || reportInfo?.is_completed === true) {
      setCompletedBookData({
        title: reportInfo.title || reportInfo.completed_book_title || '',
        author: reportInfo.author || '',
        total_pages: reportInfo.total_pages || 0,
        classification: reportInfo.classification || '',
      });
      setQuotationModalOpen(true);
      if (refreshProfile) refreshProfile();
      return;
    }
    // Normal flow: fetch a random celebration quote
    try {
      const res = await api.get('/quotes/random');
      setCelebrationQuote(res.data);
    } catch {
      setCelebrationQuote({ text: 'أحسنت! واصل مشوار القراءة', author: '' });
    }
    setCelebrationOpen(true);
    if (refreshProfile) refreshProfile();
  }, [refreshProfile]);

  // Pick up "pending celebration" flag set by BookReaderPage after submit-in-reader
  useEffect(() => {
    try {
      const raw = localStorage.getItem('pending_celebration');
      if (!raw) return;
      const data = JSON.parse(raw);
      localStorage.removeItem('pending_celebration');
      if (data) handleReportSuccess(data);
    } catch { /* ignore */ }
  }, [handleReportSuccess]);

  const navItems = useMemo(() => [
    { id: 'submit-report', label: 'رفع تقرير', icon: FilePlus2, action: 'report', accent: 'clay' },
    { id: 'profile', path: '/profile', label: 'الصفحة الرئيسة', icon: Home },
    { id: 'courses', path: '/courses', label: 'المساقات', icon: BookOpen },
    { id: 'my-courses', path: '/my-courses', label: 'منهجيتي', icon: Library },
    { id: 'articles', path: '/articles', label: 'المقالات', icon: FileText },
    { id: 'summaries', path: '/summaries', label: 'الملخصات والاقتباسات', icon: Quote },
    { id: 'productions', label: 'نتاجاتي', icon: PenLine, group: true, children: [
      { id: 'productions-upload', path: '/productions', label: 'رفع النتاج', icon: PenLine },
      { id: 'drafts', path: '/drafts', label: 'ملخصاتي', icon: FileEdit },
    ]},
    { id: 'book-questions', path: '/book-questions', label: 'أسئلة المساقات', icon: MessageCircleQuestion },
    { id: 'historical-record', path: '/historical-record', label: 'السجل التاريخي', icon: ScrollText },
    { id: 'notifications', path: '/notifications', label: 'الوارد', icon: Bell, badge: unreadCount },
  ], [unreadCount]);

  useEffect(() => {
    const active = navItems.find(n => n.group && n.children?.some(c => location.pathname === c.path));
    if (active) setOpenGroup(active.id);
  }, [location.pathname, navItems]);

  const handleLogout = () => { logout(); navigate('/login'); };
  const isCollapsed = !hoverExpanded && !mobileOpen;

  const renderNavLink = (item) => {
    const Icon = item.icon;
    // Action item (e.g. "رفع تقرير") — button that opens modal instead of Link
    if (item.action === 'report') {
      return (
        <button
          key={item.id}
          type="button"
          className={`nav-link nav-link-action nav-link-clay ${item.accent === 'clay' ? 'is-clay' : ''}`}
          data-testid={`nav-${item.id}`}
          title={item.label}
          onClick={() => { setReportModalOpen(true); setMobileOpen(false); }}
        >
          <Icon className="w-5 h-5 shrink-0" />
          <span className="nav-label">{item.label}</span>
        </button>
      );
    }
    const active = location.pathname === item.path || (item.path === '/summaries' && location.pathname.startsWith('/summaries/'));
    return (
      <Link
        key={item.id}
        to={item.path}
        className={`nav-link ${active ? 'active' : ''}`}
        data-testid={`nav-${item.id}`}
        title={item.label}
        onClick={() => setMobileOpen(false)}
      >
        <Icon className="w-5 h-5 shrink-0" />
        <span className="nav-label">{item.label}</span>
        {item.badge > 0 && (
          <span className={`nav-badge ${isCollapsed ? 'nav-badge-dot' : ''}`} title={`${item.badge} إشعار`}>
            {isCollapsed ? '' : (item.badge > 99 ? '99+' : item.badge)}
          </span>
        )}
      </Link>
    );
  };

  return (
    <div className={`admin-shell reader-shell ${isCollapsed ? 'shell-collapsed' : ''}`}>
      {/* Sidebar (right-side, RTL) */}
      <aside
        className={`admin-sidebar reader-sidebar ${isCollapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}
        onMouseEnter={() => setHoverExpanded(true)}
        onMouseLeave={() => setHoverExpanded(false)}
        data-testid="reader-sidebar"
      >
        <div className="sidebar-header">
          <Link to="/profile" className="sidebar-brand">
            <img
              src="https://customer-assets.emergentagent.com/job_anwar-reading/artifacts/9ouhjo98_%D8%A7%D9%84%D8%B4%D8%B9%D8%A7%D8%B1%20%D9%81%D9%82%D8%B7.svg"
              alt="غصن الزيتون"
              className="brand-logo"
            />
            <div className="brand-text">
              <span className="brand-title">{user?.name?.split(' ').slice(0, 2).join(' ') || 'قارئ'}</span>
              <span className="brand-sub">{user?.title || 'قارئ في المبادرة'}</span>
            </div>
          </Link>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(item => {
            if (item.group) {
              const isOpen = openGroup === item.id || isCollapsed;
              const GIcon = item.icon;
              const hasActive = item.children?.some(c => location.pathname === c.path);
              return (
                <div key={item.id} className={`nav-group ${hasActive ? 'has-active' : ''}`}>
                  <button
                    className={`nav-group-header ${isOpen ? 'open' : ''}`}
                    onClick={() => setOpenGroup(prev => prev === item.id ? '' : item.id)}
                    data-testid={`group-${item.id}`}
                  >
                    <GIcon className="w-4 h-4" />
                    <span className="nav-group-label">{item.label}</span>
                    <ChevronDown className={`w-3.5 h-3.5 chevron ${isOpen ? 'rotate' : ''}`} />
                  </button>
                  <div className={`nav-group-items ${isOpen ? 'visible' : ''}`}>
                    {item.children.map(c => renderNavLink(c))}
                  </div>
                </div>
              );
            }
            return renderNavLink(item);
          })}
        </nav>

        <div className="sidebar-footer">
          <button
            onClick={handleLogout}
            className="footer-btn danger"
            data-testid="reader-logout-btn"
            title="تسجيل الخروج"
          >
            <LogOut className="w-4 h-4 shrink-0" />
            <span>تسجيل الخروج</span>
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />}

      <div className="admin-main reader-main-no-topbar">
        {/* Mobile hamburger toggle — floating (no top bar) */}
        <button
          className={`reader-mobile-fab ${mobileOpen ? 'is-open' : ''}`}
          onClick={() => setMobileOpen(v => !v)}
          data-testid="reader-mobile-toggle"
          aria-label={mobileOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>

        {/* No floating quick-report or quick-notifications buttons.
            Both actions live inside the sidebar (and the mobile drawer). */}

        {/* Grounded mobile bottom bar — only on the reader home page.
            On other pages the action is accessible via the sidebar drawer. */}
        {location.pathname === '/profile' && (
          <div
            className={`reader-report-bottom-bar ${bottomBarMorphed ? 'is-morphed' : ''}`}
            data-testid="reader-report-bottom-bar"
            data-morphed={bottomBarMorphed ? 'true' : 'false'}
            role="region"
            aria-label="إجراءات سريعة"
          >
            <button
              type="button"
              className="reader-report-bottom-btn"
              onClick={() => setReportModalOpen(true)}
              data-testid="reader-report-bottom-btn"
              aria-label="رفع تقرير قرائي"
              title="رفع تقرير قرائي"
            >
              <Send className="w-5 h-5" />
              <span>رفع تقرير قرائي</span>
            </button>
          </div>
        )}

        <main
          className={`admin-content reader-content ${location.pathname === '/profile' ? 'has-bottom-bar' : ''}`}
        >{children}</main>
      </div>

      <ReportModal
        open={reportModalOpen}
        onClose={() => setReportModalOpen(false)}
        onSuccess={handleReportSuccess}
      />
      <CelebrationModal
        open={celebrationOpen}
        onClose={() => { setCelebrationOpen(false); }}
        quote={celebrationQuote}
      />
      <QuotationModal
        open={quotationModalOpen}
        onClose={async (submitted) => {
          setQuotationModalOpen(false);
          // Show a random celebration quote AFTER the user either saves or
          // publishes their summary (submitted=true from QuotationModal). If
          // they skipped the summary step, keep them on the page silently.
          if (submitted) {
            try {
              const res = await api.get('/quotes/random');
              setCelebrationQuote(res.data);
            } catch {
              setCelebrationQuote({ text: 'أحسنت! واصل مشوار القراءة', author: '' });
            }
            setCelebrationOpen(true);
          }
        }}
        bookData={completedBookData}
      />
      <CourseCompletionCelebration
        open={courseCelebrationOpen}
        onClose={() => setCourseCelebrationOpen(false)}
        courseTitle={courseCompletedData?.courseTitle || ''}
        readerName={user?.name || ''}
      />
      <AnnouncementModal />
    </div>
  );
};

export default ReaderLayout;
